/**
 * TableOps – Order Domain Types
 *
 * Architecture follows the existing orchestrator patterns:
 *   Scope  → Table (T1, T2, …Tn)
 *   Order  → Workflow run (one per "Place Order" action)
 *   Ticket → Workflow step (one per station that receives items)
 *   Line   → Item within a ticket
 *
 * Routing:  Order → dispatcher → splits by station → Ticket per station
 * Lifecycle: open → in_progress → ready → delivered → closed
 *
 * ┌─────────┐    dispatch     ┌───────────────────┐
 * │  Waiter │ ─────────────> │   Order (scope)    │
 * │  (FoH)  │                │   id_order: uuid   │
 * └─────────┘                │   id_scope: "T1"   │
 *                            │   lines: [...]     │
 *                            └────────┬───────────┘
 *                                     │ route by station
 *                      ┌──────────────┼──────────────┐
 *                      ▼              ▼              ▼
 *               ┌────────────┐ ┌────────────┐ ┌────────────┐
 *               │  Ticket    │ │  Ticket    │ │  Ticket    │
 *               │  KITCHEN   │ │  CAFETERIA │ │  BAR       │
 *               │  lines:[…] │ │  lines:[…] │ │  lines:[…] │
 *               └────────────┘ └────────────┘ └────────────┘
 *                      │              │              │
 *                 KDS screen     KDS screen     KDS screen
 *                 (bump done)   (bump done)    (bump done)
 *
 * Status transitions follow "bump" pattern (industry KDS standard):
 *   open → in_progress (auto on receipt)
 *   in_progress → ready (station bumps "done")
 *   ready → delivered (auto-tracked or optional waiter bump)
 *   delivered → closed (auto when all tickets closed)
 */

/* ── Stations ───────────────────────────────────────────────────────── */

export type StationId = 'kitchen' | 'cashier' | 'cafeteria' | 'bar' | 'pastry'

export const STATIONS: Record<StationId, StationMeta> = {
  kitchen:   { id: 'kitchen',   label: 'Kitchen',   icon: '🔥', color: '#ef4444' },
  cashier:   { id: 'cashier',   label: 'Cashier',   icon: '💵', color: '#14b8a6' },
  cafeteria: { id: 'cafeteria', label: 'Cafetería', icon: '☕', color: '#f59e0b' },
  bar:       { id: 'bar',       label: 'Bar',       icon: '🍷', color: '#8b5cf6' },
  pastry:    { id: 'pastry',    label: 'Pastry',    icon: '🍰', color: '#ec4899' },
}

export interface StationMeta {
  id: StationId
  label: string
  icon: string
  color: string
}

/* ── Section → Station routing map ──────────────────────────────────── */

/**
 * Maps menu sections to their target station.
 * Flexible: add new sections / stations without touching dispatch logic.
 * Unknown sections default to 'kitchen'.
 */
export const SECTION_STATION_MAP: Record<string, StationId> = {
  // Kitchen
  'Snacks':              'kitchen',
  'Platos':              'kitchen',
  'Platos Especiales':   'kitchen',
  'Brunch Menu':         'kitchen',
  'Sandwichs':           'kitchen',
  'Entradas':            'kitchen',
  'Principales':         'kitchen',
  'Tostadas y Huevos':   'kitchen',
  'A partir de las 16:00': 'kitchen',

  // Cafetería
  'Cafetería':           'cafeteria',

  // Bar
  'Sin Alcohol':         'bar',
  'Cervezas':            'bar',
  'By the Glass':        'bar',

  // Pastry
  'Dulces':              'pastry',

  // Cashier
  'Caja':                'cashier',
  'Cajas':               'cashier',
  'Cashier':             'cashier',
  'Cobros':              'cashier',
}

export function resolveStation(section: string): StationId {
  return SECTION_STATION_MAP[section] ?? 'kitchen'
}

/* ── Ticket lifecycle ───────────────────────────────────────────────── */

export type TicketStatus =
  | 'open'          // just received
  | 'in_progress'   // station acknowledged / cooking
  | 'ready'         // bumped "done" by station
  | 'delivered'     // served to table (auto or waiter bump)
  | 'closed'        // finalized
  | 'discarded'     // voided by station — terminal, shown in dark red

export const TICKET_STATUS_FLOW: TicketStatus[] = [
  'open', 'in_progress', 'ready', 'delivered', 'closed',
]

export function nextTicketStatus(current: TicketStatus): TicketStatus | null {
  const idx = TICKET_STATUS_FLOW.indexOf(current)
  if (idx < 0 || idx >= TICKET_STATUS_FLOW.length - 1) return null
  return TICKET_STATUS_FLOW[idx + 1]
}

/* ── Line / Ticket / Order ──────────────────────────────────────────── */

export interface TicketLine {
  id_item: number
  /** Stable scope key for the menu item (SKID canonical ID) */
  item_key: string
  name: string
  description: string
  price: number
  quantity: number
  /** Per-item note visible on KDS and tickets (e.g. "almond milk") */
  note?: string
}

export interface Ticket {
  id_ticket: string        // uuid
  id_order: string         // parent order
  station: StationId
  status: TicketStatus
  lines: TicketLine[]
  created_at: string       // ISO
  updated_at: string       // ISO
  bumped_at: string | null // when station bumped "ready"
}

export interface TableOrder {
  id_order: string         // uuid
  id_scope: string         // "T1", "T2", etc. OR "TA-xxxx" / "DL-xxxx" for fulfillment
  /** Which check (split bill) this order belongs to. 0-based. */
  checkIndex: number
  tickets: Ticket[]
  total: number
  status: 'open' | 'partial' | 'fulfilled' | 'closed'
  created_at: string
  updated_at: string
  /**
   * Optional fulfillment block.
   *   - Absent  → dine-in (uses parent table session for delivery + close).
   *   - Present → takeaway / delivery, with own customer + stage timestamps.
   *
   * Tickets still route to per-station KDS exactly the same way; only the
   * terminal handoff stage differs.
   */
  fulfillment?: FulfillmentInfo
}

/* ── Fulfillment (takeaway / delivery) ──────────────────────────────── */

export type FulfillmentType = 'takeaway' | 'delivery'

/**
 * Lifecycle stages for a takeaway/delivery order.
 * Each stage corresponds to a timestamp on `FulfillmentInfo`.
 *
 *   placed     → order entered in system (auto on creation)
 *   accepted   → kitchen acknowledged (auto when any ticket → in_progress)
 *   ready      → all tickets ready (auto via deriveOrderStatus = 'fulfilled')
 *   handed_off → takeaway: customer picked up │ delivery: driver departed
 *   completed  → terminal: delivered & paid (takeaway = handed_off, delivery = arrived)
 */
export type FulfillmentStage =
  | 'placed'
  | 'accepted'
  | 'ready'
  | 'handed_off'
  | 'completed'

export const FULFILLMENT_STAGE_FLOW: FulfillmentStage[] = [
  'placed', 'accepted', 'ready', 'handed_off', 'completed',
]

export interface FulfillmentInfo {
  type: FulfillmentType
  /** Customer-facing identity */
  customer_name: string
  customer_phone?: string
  /** Delivery only — free-form street address */
  address?: string
  /** Optional free-form notes (allergies, doorbell broken, etc.) */
  notes?: string
  /** ISO timestamps per stage. `placed_at` is mandatory; rest fill in as stages advance. */
  placed_at: string
  accepted_at?: string
  ready_at?: string
  handed_off_at?: string
  completed_at?: string
}

/**
 * Stage-label lookup for UI badges.
 * Kept as plain const-object (not a Map) for tree-shaking and JSON-friendliness.
 */
export const FULFILLMENT_STAGE_LABEL: Record<FulfillmentStage, string> = {
  placed:     'Placed',
  accepted:   'Accepted',
  ready:      'Ready',
  handed_off: 'Handed off',
  completed:  'Completed',
}

/**
 * Returns the next stage in the forward flow, or null if already terminal.
 * Symmetric with `nextTicketStatus`.
 */
export function nextFulfillmentStage(current: FulfillmentStage): FulfillmentStage | null {
  const i = FULFILLMENT_STAGE_FLOW.indexOf(current)
  if (i < 0 || i >= FULFILLMENT_STAGE_FLOW.length - 1) return null
  return FULFILLMENT_STAGE_FLOW[i + 1]
}

/**
 * Derive the current fulfillment stage from an order's `fulfillment` block + tickets.
 *   - Single source of truth: examines timestamps + ticket statuses; never stored separately.
 *   - Pure function — safe to call inside selectors / memo / render.
 */
export function deriveFulfillmentStage(order: TableOrder): FulfillmentStage {
  const f = order.fulfillment
  if (!f) return 'placed' // not really applicable; callers should guard
  if (f.completed_at)  return 'completed'
  if (f.handed_off_at) return 'handed_off'
  // Auto-derive ready/accepted from ticket states (avoid stamping logic drift)
  const derived = deriveOrderStatus(order.tickets)
  if (f.ready_at    || derived === 'fulfilled') return 'ready'
  if (f.accepted_at || order.tickets.some(t => t.status === 'in_progress')) return 'accepted'
  return 'placed'
}

/**
 * Stamp the timestamp for a given stage on a fulfillment block.
 * Idempotent: re-stamping an already-set stage is a no-op.
 * Returns a NEW FulfillmentInfo (immutable).
 */
export function stampFulfillmentStage(
  f: FulfillmentInfo,
  stage: FulfillmentStage,
  at?: string,
): FulfillmentInfo {
  const ts = at ?? new Date().toISOString()
  switch (stage) {
    case 'placed':     return f.placed_at      ? f : { ...f, placed_at: ts }
    case 'accepted':   return f.accepted_at    ? f : { ...f, accepted_at: ts }
    case 'ready':      return f.ready_at       ? f : { ...f, ready_at: ts }
    case 'handed_off': return f.handed_off_at  ? f : { ...f, handed_off_at: ts }
    case 'completed':  return f.completed_at   ? f : { ...f, completed_at: ts }
  }
}

/* ── Dispatch helpers ───────────────────────────────────────────────── */

let _ticketSeq = 0

function uuid(): string {
  return `${Date.now().toString(36)}-${(++_ticketSeq).toString(36)}-${Math.random().toString(36).slice(2, 6)}`
}

/**
 * Splits order lines by station and produces a full TableOrder
 * with one Ticket per station that has at least one line.
 *
 * This mirrors the orchestrator's dispatch pattern:
 *   Order → DAG → Steps routed to different workers by handler_key
 */
export function dispatchOrder(
  scopeId: string,
  lines: Array<{ id: number; scopeKey?: string; name: string; description: string; price: number; quantity: number; section: string; station?: StationId; note?: string }>,
  checkIndex: number = 0,
  fulfillment?: FulfillmentInfo,
): TableOrder {
  const now = new Date().toISOString()
  const orderId = uuid()

  // Group lines by station — prefer explicit station from menu item, fall back to section map
  const byStation = new Map<StationId, TicketLine[]>()
  for (const line of lines) {
    const station = line.station ?? resolveStation(line.section)
    if (!byStation.has(station)) byStation.set(station, [])
    byStation.get(station)!.push({
      id_item: line.id,
      item_key: line.scopeKey ?? '',
      name: line.name,
      description: line.description,
      price: line.price,
      quantity: line.quantity,
      ...(line.note ? { note: line.note } : {}),
    })
  }

  // Create tickets
  const tickets: Ticket[] = []
  byStation.forEach((ticketLines, station) => {
    tickets.push({
      id_ticket: uuid(),
      id_order: orderId,
      station,
      status: 'in_progress',   // auto-progress on dispatch (no manual ack needed)
      lines: ticketLines,
      created_at: now,
      updated_at: now,
      bumped_at: null,
    })
  })

  const total = lines.reduce((s, l) => s + l.price * l.quantity, 0)

  return {
    id_order: orderId,
    id_scope: scopeId,
    checkIndex,
    tickets,
    total,
    status: 'open',
    created_at: now,
    updated_at: now,
    ...(fulfillment ? { fulfillment } : {}),
  }
}

/**
 * Derive order-level status from its tickets.
 *   all closed   → 'closed'
 *   all ready+   → 'fulfilled'
 *   some ready+  → 'partial'
 *   else         → 'open'
 */
export function deriveOrderStatus(tickets: Ticket[]): TableOrder['status'] {
  if (tickets.length === 0) return 'closed'
  const readyPlus = (s: TicketStatus) => s === 'ready' || s === 'delivered' || s === 'closed'
  if (tickets.every(t => t.status === 'closed')) return 'closed'
  if (tickets.every(t => readyPlus(t.status))) return 'fulfilled'
  if (tickets.some(t => readyPlus(t.status))) return 'partial'
  return 'open'
}
